import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { HomepageResponse } from '../types/homepage';

// Environment-based API configuration
const getApiBaseUrl = () => {
  const configuredUrl = import.meta.env.VITE_API_BASE_URL;

  if (!configuredUrl && import.meta.env.PROD) {
    throw new Error('VITE_API_BASE_URL must be set for production builds');
  }

  return configuredUrl || 'http://localhost:8080/api/v1';
};

const API_BASE_URL = getApiBaseUrl();
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');

// Environment validation for production  
if (API_BASE_URL.includes('localhost') && import.meta.env.PROD) {
  console.error('🚨 PRODUCTION ERROR: API configured with localhost URL!');
  console.error('Actual:', API_BASE_URL);
  console.error('Environment variables:', {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_NODE_ENV: import.meta.env.VITE_NODE_ENV,
    NODE_ENV: import.meta.env.NODE_ENV,
    PROD: import.meta.env.PROD
  });
}

// Debug logging for production to verify API URL
if (import.meta.env.PROD) {
  console.log('🔗 Frontend configured to use API:', API_BASE_URL);
}

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
  type: 'network' | 'service_outage' | 'server_error' | 'validation' | 'auth' | 'unknown';
  isRetryable: boolean;
}

export class ApiClient {
  private client: AxiosInstance;
  private refreshing = false;
  private refreshFailed = false; // Prevents multiple refresh attempts after failure
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];
  private onAuthError?: (error: ApiError) => void;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Axios exposes an error JSON body as a Blob when a binary request
        // uses responseType=blob. Normalize only bounded 401/403 JSON bodies
        // before the existing #238 refresh logic inspects TOKEN_INVALID.
        if (
          typeof Blob !== 'undefined' &&
          error?.response &&
          (error.response.status === 401 || error.response.status === 403) &&
          error.response.data instanceof Blob
        ) {
          await this.normalizeBlobError(error);
        }

        // Check for token expiration: 401 OR 403 with TOKEN_INVALID code
        const isTokenExpired = error.response?.status === 401 ||
          (error.response?.status === 403 && error.response?.data?.code === 'TOKEN_INVALID');
        const isLoginRequest = this.isLoginRequest(originalRequest);

        // A rejected credential submission is an authentication response, but
        // it is not an expired authenticated session. Let the login page keep
        // the backend error and its current returnTo URL instead of attempting
        // a refresh with the previous session's token (or redirecting away).
        if (isTokenExpired && !isLoginRequest && originalRequest && !originalRequest._retry) {
          // If refresh already failed, don't attempt again (prevents race during redirect delay)
          if (this.refreshFailed) {
            return Promise.reject(error);
          }

          if (this.refreshing) {
            // Every request joins the current refresh at most once. Mark the
            // queued retry before waiting so a second 401 cannot begin another
            // refresh cycle when that retry is rejected.
            originalRequest._retry = true;
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            });
          }

          originalRequest._retry = true;
          this.refreshing = true;

          try {
            const newToken = await this.refreshAuthToken();
            this.processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.refreshFailed = true; // Prevent further refresh attempts during redirect delay
            this.processQueue(refreshError, null);
            this.clearAuthTokens();

            // Emit authentication error before redirect
            const authError = this.formatError(refreshError);
            if (this.onAuthError) {
              this.onAuthError(authError);

              // Add a small delay to allow error notification to show
              setTimeout(() => {
                window.location.href = '/admin/login';
              }, 1000);
            } else {
              window.location.href = '/admin/login';
            }

            return Promise.reject(refreshError);
          } finally {
            this.refreshing = false;
          }
        }

        const formattedError = this.formatError(error, isLoginRequest);

        // Notify the app shell for session authentication errors. Credential
        // failures belong to the login form and should not create a second,
        // global "authentication required" notification.
        if (!isLoginRequest && formattedError.type === 'auth' && this.onAuthError) {
          this.onAuthError(formattedError);
        }

        return Promise.reject(formattedError);
      }
    );
  }

  private async normalizeBlobError(error: unknown): Promise<void> {
    const response = typeof error === 'object' && error !== null && 'response' in error
      ? (error as { response?: { status?: number; data?: unknown } }).response
      : undefined;
    const body = response?.data;
    if (
      !response ||
      (response.status !== 401 && response.status !== 403) ||
      typeof Blob === 'undefined' ||
      !(body instanceof Blob) ||
      body.size > 64 * 1024
    ) {
      return;
    }

    try {
      const text = typeof body.text === 'function'
        ? await body.text()
        : await new Promise<string>((resolve) => {
          if (typeof FileReader === 'undefined') {
            resolve('');
            return;
          }
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
          reader.onerror = () => resolve('');
          reader.readAsText(body);
        });
      if (!text || text.length > 64 * 1024) return;
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') {
        response.data = parsed;
      }
    } catch {
      // Keep the Blob so formatError can still produce the status fallback.
    }
  }

  private isLoginRequest(config?: AxiosRequestConfig): boolean {
    const requestUrl = config?.url;
    const baseUrl = config?.baseURL || API_BASE_URL;

    if (!requestUrl) {
      return false;
    }

    try {
      const resolvedBaseUrl = new URL(baseUrl);
      // Axios combines relative request URLs with baseURL by trimming the
      // base trailing slash and request leading slash. Use that same join
      // before comparing paths; URL's standard relative resolution would
      // discard a configured base path for a request such as /auth/login.
      const combinedUrl = /^([a-z][a-z\d+\-.]*:)?\/\//i.test(requestUrl)
        ? requestUrl
        : `${baseUrl.replace(/\/+$/, '')}/${requestUrl.replace(/^\/+/, '')}`;
      const resolvedUrl = new URL(combinedUrl);
      const basePath = resolvedBaseUrl.pathname.replace(/\/+$/, '');
      const requestPath = resolvedUrl.pathname.replace(/\/+$/, '');

      return resolvedUrl.origin === resolvedBaseUrl.origin &&
        requestPath === `${basePath}/auth/login`;
    } catch {
      return false;
    }
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });
    this.failedQueue = [];
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private async refreshAuthToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken: refreshToken,
    });

    const { tokens } = response.data;
    const { accessToken } = tokens;
    localStorage.setItem('auth_token', accessToken);
    return accessToken;
  }

  private clearAuthTokens() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  }

  private formatError(error: any, preserveCredentialError = false): ApiError {
    if (error.response) {
      const status = error.response.status;
      const responseMessage = error.response.data?.message ||
        (preserveCredentialError && status === 401 ? error.response.data?.error : undefined);
      const message = typeof responseMessage === 'string'
        ? responseMessage
        : this.getDefaultErrorMessage(status);

      // Detect Railway service outage (404 "Application not found")
      const isRailwayOutage = status === 404 &&
        (message.includes('Application not found') ||
          message.includes('Not Found') ||
          error.response.headers?.server === 'Railway');

      if (isRailwayOutage) {
        return {
          message: 'Service temporarily unavailable. Our backend is currently experiencing issues. Please try again in a few minutes.',
          status,
          errors: error.response.data?.errors,
          type: 'service_outage',
          isRetryable: true,
        };
      }

      // Categorize other HTTP errors
      let type: ApiError['type'] = 'unknown';
      let isRetryable = false;

      if (status === 401 || status === 403) {
        type = 'auth';
        isRetryable = false;
      } else if (status === 422 || status === 400) {
        type = 'validation';
        isRetryable = false;
      } else if (status >= 500) {
        type = 'server_error';
        isRetryable = true;
      }

      return {
        message,
        status,
        errors: error.response.data?.errors,
        type,
        isRetryable,
      };
    } else if (error.request) {
      // Network error (no response received)
      return {
        message: 'Unable to connect to our servers. Please check your internet connection and try again.',
        status: 0,
        type: 'network',
        isRetryable: true,
      };
    } else {
      // Other errors (request setup, etc.)
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
        type: 'unknown',
        isRetryable: false,
      };
    }
  }

  private getDefaultErrorMessage(status: number): string {
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Please check your input and correct any errors.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again in a few minutes.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a few minutes.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  // Public API methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get<Blob>(url, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // File upload with progress
  async uploadFile<T>(
    url: string,
    file: File,
    options?: {
      onProgress?: (progress: number) => void;
      additionalData?: Record<string, any>;
    }
  ): Promise<T> {
    const formData = new FormData();
    formData.append('image', file);

    if (options?.additionalData) {
      Object.entries(options.additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          options.onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Batch upload with individual progress tracking
  async uploadFiles<T>(
    url: string,
    files: File[],
    options?: {
      onProgress?: (fileIndex: number, progress: number) => void;
      onComplete?: (fileIndex: number, result: T) => void;
      onError?: (fileIndex: number, error: ApiError) => void;
      additionalData?: Record<string, any>;
    }
  ): Promise<Array<{ success: boolean; data?: T; error?: ApiError }>> {
    const results = await Promise.allSettled(
      files.map((file, index) =>
        this.uploadFile<T>(url, file, {
          onProgress: (progress) => options?.onProgress?.(index, progress),
          additionalData: options?.additionalData,
        }).then((data) => {
          options?.onComplete?.(index, data);
          return data;
        })
      )
    );

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return { success: true, data: result.value };
      } else {
        const error = this.formatError(result.reason);
        options?.onError?.(index, error);
        return { success: false, error };
      }
    });
  }

  // Utility methods
  setAuthTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    this.refreshFailed = false; // Reset on successful login
  }

  clearAuth() {
    this.clearAuthTokens();
  }

  setAuthErrorHandler(handler: (error: ApiError) => void) {
    this.onAuthError = handler;
  }

  removeAuthErrorHandler() {
    this.onAuthError = undefined;
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Get image URL for BYTEA data with version-based cache busting
  getImageUrl(artworkId: number, type: 'image' | 'thumbnail' = 'image', updatedAt?: string | Date): string {
    const baseUrl = `${API_BASE_URL}/artworks/${artworkId}/${type}`;

    if (updatedAt) {
      // Convert Date to timestamp for version parameter
      const version = updatedAt instanceof Date
        ? updatedAt.getTime()
        : new Date(updatedAt).getTime();
      return `${baseUrl}?v=${version}`;
    }

    return baseUrl;
  }

  // Get the authoritative homepage bundle. Legacy fallback payloads are not
  // valid homepage data and must reach the page error state.
  async getHomepageData(): Promise<HomepageResponse> {
    const response = await this.get<HomepageResponse>('/homepage-data');
    const legacyMeta = response?.meta as unknown as { fallback?: boolean; error?: string } | undefined;
    const collectionSection = response?.data?.collectionSection;
    if (
      legacyMeta?.fallback === true ||
      typeof legacyMeta?.error === 'string' ||
      !collectionSection ||
      !['available', 'unavailable'].includes(collectionSection.status) ||
      !Array.isArray(response.data.featuredExhibitions) ||
      !Array.isArray(response.data.gridExhibitions) ||
      !response.data.hero ||
      !Array.isArray(response.data.hero.artworks) ||
      !response.meta
    ) {
      throw new Error('Homepage data is temporarily unavailable. Please try again.');
    }
    return response;
  }
}

// Singleton instance
export const apiClient = new ApiClient();
export default apiClient;
